import EventEmitter2 from 'eventemitter2';
import type { RxCollection, RxStorage } from 'rxdb';
import { createRxDatabase, removeRxDatabase } from 'rxdb';
import * as rxAjv from 'rxdb/plugins/validate-ajv';
import * as rxZSchema from 'rxdb/plugins/validate-z-schema';

/* eslint-disable import/no-cycle, restrict-imports/restrict-imports */
import migrateStateSyncingFromLocalForage from '../background/database/migrateStateSyncingFromLocalForage';
import migrateStateSyncingToNewPersistentStateBlob from '../background/database/migrateStateSyncingToNewPersistentStateBlob';
import { setActiveSchemaMigration } from '../foreground/stateUpdaters/clientStateUpdaters/other';
/* eslint-enable import/no-cycle, restrict-imports/restrict-imports */
import type {
  DatabaseCollection,
  DatabaseCollectionNamesToDocType,
  RxCollections,
  RxDBInstance,
} from '../types/database';
import type {
  HandleStateUpdateSideEffectsParameter,
  HandleStateUpdateSideEffectsResult,
} from '../types/stateUpdates';
import { DeferredPromise } from '../utils/DeferredPromise';
import {
  isDevOrTest,
  isDocumentShareApp,
  isExtension,
  isMobile,
  isTest,
  isUsingSQLite,
  isWebApp,
} from '../utils/environment';
import exceptionHandler from '../utils/exceptionHandler.platform';
import type { MaybePromise } from '../utils/typescriptUtils';
import clearLocalRxdbData from './clearLocalRxdbData.platform';
import defineSchema, { type schema } from './internals/defineSchema';
import getDatabaseCollectionFromRxCollection from './internals/getDatabaseCollectionFromRxCollection';
import getDatabaseName from './internals/getDatabaseName';
import getRxStorage from './internals/getRxStorage.platformIncludingExtension';
import logger from './internals/logger';
import createManualIndexes from './internals/manualIndexes';
import addMiddleware from './internals/middleware/addMiddleware';
import { finishStorageMigration, startStorageMigration } from './internals/migration';
import setUpPlugins from './internals/setUpPlugins';
import { wrappedSQLiteDocumentsStorage } from './SQLiteDocumentsStorageWrapper';

type Collections = {
  [key in keyof typeof schema]: DatabaseCollection<key>;
};

type Options = {
  additionalMiddleware?: (database: Database) => MaybePromise<void>;
  allowSlowCount?: boolean;
  eventEmitter: EventEmitter2;
  handleStateUpdateSideEffects: (
    arg: HandleStateUpdateSideEffectsParameter,
  ) => HandleStateUpdateSideEffectsResult;
  // NOTE: in the web app, Database is instantiated once for both background and foreground
  isInForeground: boolean;
};

export default class Database {
  collections: Collections = this._getCollections();
  // eslint-disable-next-line @typescript-eslint/naming-convention
  collections__force: Collections = this._getCollections(true);
  rxDbInstance: RxDBInstance | null = null;

  _databaseName = getDatabaseName();
  _initializationPromise: DeferredPromise<RxDBInstance> | null = null;
  _options: Options;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  _storage: RxStorage<any, any> | null = null;

  constructor(options: Options) {
    logger.debug('Constructor');
    this._options = options;
    setUpPlugins();
  }

  assignOptions(options: Partial<Options>): void {
    Object.assign(this._options, options);
  }

  async clearUninitializedDb() {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return removeRxDatabase(this._databaseName, this._storage);
  }

  async clear() {
    if (this._initializationPromise) {
      await this._initializationPromise;
    }
    if (isMobile) {
      exceptionHandler.addBreadcrumb({
        type: 'debug',
        message: `clear database called`,
      });
    }
    if (!this.rxDbInstance || !this._storage) {
      this.rxDbInstance = await this._createDatabase();
    }
    if (!this._storage) {
      throw Error("Somehow storage cannot be created for db, can't clear it.");
    }

    await removeRxDatabase(this._databaseName, this._storage);

    await this.destroyRxDBInstance();

    await clearLocalRxdbData();

    logger.debug('Cleared');
  }

  async destroyRxDBInstance() {
    if (!this.rxDbInstance) {
      return;
    }
    this._initializationPromise = null;
    if (isMobile) {
      exceptionHandler.addBreadcrumb({
        type: 'debug',
        message: `destroy RxDBInstance`,
      });
    }
    const promise = this.rxDbInstance.destroy();
    this.rxDbInstance = null;
    if (isMobile) {
      exceptionHandler.addBreadcrumb({
        type: 'debug',
        message: `RxDBInstance destroyed, rxDbInstance set to null`,
      });
    }
    try {
      await promise;
    } catch (error) {
      logger.error('Error destroying RxDB instance', { error });
    }
  }

  getRawCollection<T extends keyof DatabaseCollectionNamesToDocType>(collectionName: T) {
    if (!this.isInitialized()) {
      throw new Error('database is not initialized (yet?)');
    }
    if (!this.rxDbInstance) {
      throw new Error('this.rxDbInstance undefined');
    }
    if (!this.rxDbInstance.collections[collectionName]) {
      throw new Error(`this.rxDbInstance.collections.${collectionName} does not exist`);
    }
    return this.rxDbInstance.collections[collectionName] as unknown as RxCollection<DatabaseCollectionNamesToDocType[T]>;
  }

  getRxDBInstance() {
    if (!this.rxDbInstance) {
      throw new Error('Database not initialized');
    }
    return this.rxDbInstance;
  }

  shouldMigrateOldStateStorage() {
    return !isExtension && !isDocumentShareApp || !this._options.isInForeground;
  }

  async initialize({
    onSchemaConflictError,
  }: { onSchemaConflictError?: () => Promise<void>; } = {}): Promise<RxDBInstance> {
    if (this._initializationPromise) {
      return this._initializationPromise;
    }

    this._initializationPromise = new DeferredPromise<RxDBInstance>();
    if (isMobile) {
      exceptionHandler.addBreadcrumb({
        type: 'debug',
        message: `db initialize`,
      });
    }

    /*
     * Start storage migration code before we start the database, as we can't
     * properly tear down a database created with the wrong storage.
     */
    const isClientAllowedToMigrateStorage = !(isExtension && this._options.isInForeground) && !isTest;
    if (isClientAllowedToMigrateStorage) {
      try {
        await startStorageMigration();
      } catch (error) {
        if (isMobile) {
          exceptionHandler.addBreadcrumb({
            type: 'debug',
            message: `db initialize - migration failed`,
          });
        }
        logger.error('Starting the migration failed', { error });
        this._initializationPromise.reject(error as Error);
        throw error;
      }
    }

    try {
      this.rxDbInstance = await this._createDatabase();
    } catch (error) {
      if (isMobile) {
        exceptionHandler.addBreadcrumb({
          type: 'debug',
          message: `db initialize - creation failed`,
        });
      }
      logger.error('Creation failed', { error });
      this._initializationPromise.reject(error as Error);
      throw error;
    }

    await setActiveSchemaMigration(true);
    try {
      try {
        await defineSchema(this.rxDbInstance, this._options.isInForeground);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        if (isMobile) {
          exceptionHandler.addBreadcrumb({
            type: 'debug',
            message: `db initialize - Error thrown while defining schema`,
          });
        }
        logger.error('Error thrown while defining schema', { error });
        if ((isWebApp || isMobile) && error.message.includes('RxError (DB6)') && onSchemaConflictError) {
          await onSchemaConflictError();
          return this._initializationPromise;
        }

        this._initializationPromise.reject(error as Error);
        throw error;
      }

      await addMiddleware(this, this._options.additionalMiddleware);

      if (this.shouldMigrateOldStateStorage()) {
        try {
          await migrateStateSyncingFromLocalForage({
            rxDBInstance: this.rxDbInstance,
          });
        } catch (error) {
          if (isMobile) {
            exceptionHandler.addBreadcrumb({
              type: 'debug',
              message: `db initialize - Error thrown while migrating from localForage`,
            });
          }
          logger.warn('Error thrown while migrating from localForage, ignoring...', { error });
        }

        try {
          await migrateStateSyncingToNewPersistentStateBlob(this);
        } catch (error) {
          if (isMobile) {
            exceptionHandler.addBreadcrumb({
              type: 'debug',
              message: `db initialize - Error thrown while migrating from persistentStateBlob to documents`,
            });
          }
          logger.error('Error thrown while migrating from persistentStateBlob to documents', { error });
          throw error;
        }
      }

      // finish ongoing storage migration
      if (isClientAllowedToMigrateStorage) {
        try {
          await finishStorageMigration(this.rxDbInstance);
        } catch (error) {
          if (isMobile) {
            exceptionHandler.addBreadcrumb({
              type: 'debug',
              message: `db initialize - Finishing the migration failed`,
            });
          }
          logger.error('Finishing the migration failed', { error });
          this._initializationPromise.reject(error as Error);
          throw error;
        }
      }
    } finally {
      // Ensure we never get stuck in the active migration state, even if an error is thrown
      await setActiveSchemaMigration(false);
    }

    this._initializationPromise.resolve(this.rxDbInstance);

    // Create manual indexes asynchronously after initialization completes
    // This doesn't block database initialization since these indexes are only
    // used by the Offline Documents settings screen currently
    if (isUsingSQLite) {
      this._createManualIndexesAsync(this.rxDbInstance);
    }
    if (isMobile) {
      exceptionHandler.addBreadcrumb({
        type: 'debug',
        message: `db initialize - returned rxDbInstance`,
      });
    }
    return this.rxDbInstance;
  }

  isInitialized(): boolean {
    return this._initializationPromise?.status === 'resolved';
  }

  async _createDatabase() {
    this._storage = getRxStorage({
      eventEmitter: this._options.eventEmitter,
      isInForeground: this._options.isInForeground,
    });
    if (isMobile) {
      exceptionHandler.addBreadcrumb({
        type: 'debug',
        message: `_createDatabase - getRxStorage passed`,
      });
    }
    // We must wrap our custom SQLite storage here before applying key compression, or else we query on uncompressed keys.
    if (isUsingSQLite) {
      this._storage = wrappedSQLiteDocumentsStorage({ storage: this._storage });
    }
    if (isMobile) {
      exceptionHandler.addBreadcrumb({
        type: 'debug',
        message: `_createDatabase - wrappedSQLiteDocumentsStorage passed`,
      });
    }
    if (
      isDevOrTest &&
      !isExtension // This has internal dynamic imports which isn't compatible with the extension build system
    ) {

      /*
        wrappedValidateAjvStorage doesnt work with hermes currently, we want to use ajv validation when it gets fixed.
        See https://github.com/ajv-validator/ajv/issues/1945
      */
      if (isMobile) {
        this._storage = rxZSchema.wrappedValidateZSchemaStorage({ storage: this._storage });
      } else {
        this._storage = rxAjv.wrappedValidateAjvStorage({ storage: this._storage });
      }
    }

    const minute = 1000 * 60;
    const month = minute * 60 * 24 * 31;

    const result = await createRxDatabase<RxCollections>({
      allowSlowCount: true,
      cleanupPolicy: {
        awaitReplicationsInSync: true,
        // https://rxdb.info/cleanup.html
        // Minimum time since item was deleted
        minimumDeletedTime: month,
        // Delay before first run
        minimumCollectionAge: 2 * minute,
        // Delay before subsequent runs
        runEach: 5 * minute,
        waitForLeadership: !isExtension,
      },
      eventReduce: true,
      ignoreDuplicate: false,
      multiInstance: !isExtension && !isMobile && !isTest,
      name: this._databaseName,
      storage: this._storage,
    });
    if (isMobile) {
      exceptionHandler.addBreadcrumb({
        type: 'debug',
        message: `DB Created`,
      });
    }
    logger.debug('Created');
    return result;
  }

  _createManualIndexesAsync(rxDbInstance: RxDBInstance): void {
    // Run manual index creation asynchronously without blocking initialization
    createManualIndexes(rxDbInstance).catch((error) => {
      logger.warn('Failed to create manual indexes, continuing without them', { error });
    });
  }

  _getCollections(force?: boolean): Collections {
    return new Proxy(
      {},
      {
        get: (target, propertyName: string) => {
          if (!this.isInitialized() && !force) {
            throw new Error('database is not initialized (yet?)');
          }
          if (!this.rxDbInstance) {
            throw new Error('this.rxDbInstance undefined');
          }
          // claude eslint: Proxy get trap — propertyName is a dynamic string from proxy, no static collection key available
          // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
          if (!(this.rxDbInstance.collections as any)[propertyName]) {
            throw new Error(`this.rxDbInstance.collections.${propertyName} does not exist`);
          }

          return getDatabaseCollectionFromRxCollection(
            // claude eslint: Proxy get trap — propertyName is a dynamic string from proxy, no static collection key available
            // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
            (this.rxDbInstance.collections as any)[propertyName],
            this._options.handleStateUpdateSideEffects,
          );
        },
      },
    ) as Collections;
  }
}
